import { logger } from '../../utils/common/logger'
import { DOMUtils } from '../../utils/dom'
import { IWidgetAdapter } from './adapters/adapter'
import { Distroscale } from './adapters/distroscale'
import { FlyerCity } from './adapters/flyercity'
import { Markets } from './adapters/markets'
import { Origami } from './adapters/origami'
import { OwnLocal } from './adapters/ownlocal'
import { Slimcut } from './adapters/slimcut'
import { Subscribe } from './adapters/subscribe'

export enum WidgetType {
    DISTROSCALE = 'distroscale',
    FLYERCITY = 'flyercity',
    OWNLOCAL = 'ownlocal',
    ORIGAMI = 'origami',
    SLIMCUT = 'slimcut',
    MARKETS = 'markets',
    SUBSCRIBE = 'subscribe'
}

export class WidgetService {
    public static DOM = {
        attrs: {
            widget: 'data-widget'
        }
    }

    // Config for IntersectionObserver
    private static IO_CONFIG = {
        rootMargin: '50%', // trigger IO callback slightly before the container comes into viewport
        threshold: 0
    }

    public widgets: IWidgetAdapter[] = []

    /**
     * Creates a new widget instance based on a passed target element's widget attribute
     * @param target
     * @param adLight
     * @private
     */
    public static createWidgetInstance(target: Element, adLight: boolean) {
        switch (target.getAttribute(WidgetService.DOM.attrs.widget)) {
            case WidgetType.DISTROSCALE:
                return new Distroscale(target, adLight)
            case WidgetType.FLYERCITY:
                return new FlyerCity(target, adLight)
            case WidgetType.ORIGAMI:
                return new Origami(target, adLight)
            case WidgetType.OWNLOCAL:
                return new OwnLocal(target, adLight)
            case WidgetType.SLIMCUT:
                return new Slimcut(target, adLight)
            case WidgetType.MARKETS:
                return new Markets(target)
            case WidgetType.SUBSCRIBE:
                return new Subscribe(target, adLight)
        }
        return undefined
    }

    /**
     * Initialize widget service by observing widgets with an Intersection Observer to initialize them when in viewport
     * @param {boolean} adLight - adlight status
     */
    public async initialize(adLight: boolean) {
        await DOMUtils.ready
        this.observeWidgets(adLight)
        logger('info', 'Widget service initialized, listening for all ads requested to start observing widgets')
    }

    /**
     *  update the widgets exposed to ad-lite in the DOM
     */
    public async update(adLight: boolean) {
        if (adLight) {
            for (const widget of this.widgets) {
                widget.removeWidgetContainer()
            }
        }
    }

    /**
     * Create Intersection Observer for each widget on page and load only when widget is in viewport
     * @param adLight
     * @private
     */
    private observeWidgets(adLight: boolean) {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[], self: IntersectionObserver) => {
                entries.forEach((entry: IntersectionObserverEntry) => {
                    // tslint:disable-next-line: no-unnecessary-initializer
                    let widget = undefined
                    const target = entry.target
                    if (entry.isIntersecting) {

                        logger('info', 'Widget intersected, instancing new widget')

                        widget = WidgetService.createWidgetInstance(target, adLight)

                        if (widget) {
                            this.widgets.push(widget)
                        }

                        // stop observing widget if intersected to prevent re-loading of widget
                        self.unobserve(entry.target)
                    }
                    return widget
                })
            }, WidgetService.IO_CONFIG)

        // Observe all widgets on page
        DOMUtils.nodes(document.querySelectorAll(`[${WidgetService.DOM.attrs.widget}]`))
            .forEach((element: Element) => {
                observer.observe(element)
            })
        logger('info', 'Intersection Observer created, observing all widgets on page for intersection')

    }
}
